import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import { transportistaService, entidadService } from '../services/api';
import '../styles/datatable.css';
import { getErrorDetail } from '../utils/errorHelper';

const TIPOS_TRANSPORTISTA = [
  { codigo: '', label: '-- Seleccionar --' },
  { codigo: '01', label: '01 - Público' },
  { codigo: '02', label: '02 - Privado' },
];

const getTipoLabel = (codigo) => {
  const t = TIPOS_TRANSPORTISTA.find(t => t.codigo === codigo);
  return t && t.codigo ? t.label : '--';
};

const Transportistas = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [consultando, setConsultando] = useState(false);

  const emptyForm = { codigo: '', ruc: '', razon_social: '', autorizacion_mtc: '', tipo_transportista: '' };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await transportistaService.getAll();
      setRecords(res.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar transportistas.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    window._dtImportHandler = async (rows) => {
      let ok = 0, fail = 0;
      const total = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        const razon_social = row['Razon Social'] || row['razon_social'] || '';
        let errorMsg = null;
        if (!razon_social) {
          fail++; errorMsg = `Fila ${i + 1}: Razon Social vacia (obligatoria)`;
        } else {
          try {
            await transportistaService.create({
              codigo: row['Codigo'] || row['codigo'] || null,
              ruc: row['RUC'] || row['ruc'] || null,
              razon_social,
              autorizacion_mtc: row['Autorizacion MTC'] || row['autorizacion_mtc'] || null,
              tipo_transportista: row['Tipo'] || row['tipo_transportista'] || null,
            });
            ok++;
          } catch (err) {
            fail++;
            errorMsg = `Fila ${i + 1} ("${razon_social}"): ${getErrorDetail(err, 'Error al crear transportista')}`;
          }
        }
        if (window._dtImportProgress) window._dtImportProgress(i + 1, total, ok, fail, errorMsg);
      }
      fetchData();
    };
    return () => { window._dtImportHandler = null; };
  }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      codigo: r.codigo || '',
      ruc: r.ruc || '',
      razon_social: r.razon_social || '',
      autorizacion_mtc: r.autorizacion_mtc || '',
      tipo_transportista: r.tipo_transportista || '',
    });
    setShowModal(true);
  };

  const handleConsultarRuc = async () => {
    if (!form.ruc || form.ruc.length !== 11) {
      setAlert({ type: 'warning', message: 'Ingrese un RUC válido de 11 dígitos.' });
      return;
    }
    setConsultando(true);
    try {
      const res = await entidadService.consultarRuc(form.ruc);
      setForm(prev => ({
        ...prev,
        razon_social: res.data.razon_social || prev.razon_social,
      }));
    } catch (err) {
      setAlert({ type: 'error', message: err.response?.data?.detail || 'No se encontraron datos para ese RUC.' });
    } finally {
      setConsultando(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.razon_social.trim()) {
      setAlert({ type: 'warning', message: 'La razón social es obligatoria.' });
      return;
    }
    if (form.codigo && !/^\d{4}$/.test(form.codigo.trim())) {
      setAlert({ type: 'warning', message: 'El código debe tener exactamente 4 dígitos numéricos (ej: 0001, 0123).' });
      return;
    }
    const data = {};
    Object.entries(form).forEach(([k, v]) => { data[k] = v === '' ? null : v; });
    try {
      if (editing) await transportistaService.update(editing.id_transportista, data);
      else await transportistaService.create(data);
      setShowModal(false);
      fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Transportista',
      message: `¿Eliminar "${r.razon_social}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await transportistaService.delete(r.id_transportista);
          setToast('Transportista eliminado');
          fetchData();
        } catch {
          setAlert({ type: 'error', message: 'Error al eliminar.' });
        }
      },
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const columns = [
    { key: 'codigo', label: 'Código', width: 80, style: { fontWeight: 600, textAlign: 'center' } },
    { key: 'ruc', label: 'RUC', width: 120 },
    { key: 'razon_social', label: 'Razón Social' },
    { key: 'autorizacion_mtc', label: 'Autorización MTC', width: 170 },
    { key: 'tipo_transportista', label: 'Tipo', width: 120, render: (r) => getTipoLabel(r.tipo_transportista) },
  ];

  const importCols = [
    { key: 'Codigo', label: 'Código', required: false, example: '0001', description: 'Código de 4 dígitos del transportista' },
    { key: 'RUC', label: 'RUC', required: false, example: '20100000001', description: 'Número de RUC del transportista' },
    { key: 'Razon Social', label: 'Razón Social', required: true, example: 'Transportes Lima S.A.C.', description: 'Razón social del transportista' },
    { key: 'Autorizacion MTC', label: 'Autorización MTC', required: false, example: 'MTC-2024-00123', description: 'Número de autorización del MTC' },
    { key: 'Tipo', label: 'Tipo', required: false, example: '01', description: 'Tipo de transportista: 01=Público, 02=Privado' },
  ];

  return (
    <Layout title="Transportistas" subtitle="Entidades">
      <div className="page-header">
        <h2>Transportistas</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable
        columns={columns} data={records} idField="id_transportista"
        onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay transportistas registrados"
        exportFileName="transportistas" importColumns={importCols}
      />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2>{editing ? 'Editar Transportista' : 'Nuevo Transportista'}</h2>
                {!editing && (
                  <span className="modal-header-badge">
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                {/* Fila: Código + Tipo */}
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: '0 16px' }}>
                  <div className="form-row">
                    <label>Código</label>
                    <input
                      name="codigo"
                      value={form.codigo}
                      onChange={handleChange}
                      placeholder="0001"
                      maxLength={4}
                      autoFocus
                      style={{ fontFamily: 'monospace', letterSpacing: 2 }}
                    />
                  </div>
                  <div className="form-row">
                    <label>Tipo de Transportista</label>
                    <select name="tipo_transportista" value={form.tipo_transportista} onChange={handleChange}>
                      {TIPOS_TRANSPORTISTA.map(t => (
                        <option key={t.codigo} value={t.codigo}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Fila: RUC + botón buscar + Razón Social */}
                <div style={{ display: 'grid', gridTemplateColumns: '150px auto 1fr', gap: '0 10px', alignItems: 'flex-end' }}>
                  <div className="form-row">
                    <label>RUC</label>
                    <input
                      name="ruc"
                      value={form.ruc}
                      onChange={handleChange}
                      placeholder="20100000001"
                      maxLength={11}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleConsultarRuc}
                    disabled={consultando}
                    title="Consultar RUC en SUNAT"
                    style={{ marginBottom: 18, padding: '0 12px', height: 36, display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                  >
                    {consultando ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 20 20" fill="currentColor" width="15" height="15">
                        <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                      </svg>
                    )}
                    Buscar
                  </button>
                  <div className="form-row">
                    <label>Razón Social *</label>
                    <input
                      name="razon_social"
                      value={form.razon_social}
                      onChange={handleChange}
                      placeholder="Razón social del transportista"
                    />
                  </div>
                </div>

                {/* Autorización MTC */}
                <div className="form-row">
                  <label>Número de Autorización MTC</label>
                  <input
                    name="autorizacion_mtc"
                    value={form.autorizacion_mtc}
                    onChange={handleChange}
                    placeholder="Ej: MTC-2024-00123"
                  />
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </Layout>
  );
};

export default Transportistas;
